import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import { Globals } from '../globalfunctions.js';
import { apiClient, ApiError } from '../utils/apiClient';
import './Items.css';
import { confirmDialog, alertDialog } from '../utils/dialogs';

function Items() {
  const navigate = useNavigate();
  const [displayItems, setDisplayItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [banner, setBanner] = useState(null);
  const isSysadmin = (Globals.UserType || '').toLowerCase() === 'sysadmin';

  useEffect(() => {
    if (Globals.LoginTime === null) navigate('/login');
  }, [navigate]);

  useEffect(() => {
    async function fetchItems() {
      setLoading(true);
      try {
        const data = await apiClient.get('/api/PurchasableItems');
        setDisplayItems(data.map((item, index) => ({ ...item, _key: index })));
      } catch (error) {
        console.error('Error fetching items:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchItems();
  }, []);

  const startEdit = (item) => {
    setBanner(null);
    setEditingId(item.id);
    setEditValue(item.item_price);
  };

  const cancelEdit = () => setEditingId(null);

  const confirmAndSavePrice = async (item) => {
    const value = parseFloat(editValue);
    if (Number.isNaN(value) || value < 0) {
      alertDialog('Enter a valid, non-negative price.');
      return;
    }

    const nextPrice = value.toFixed(2);
    const previousPrice = Number(item.item_price).toFixed(2);
    if (nextPrice === previousPrice) {
      setEditingId(null);
      return;
    }

    const confirmed = await confirmDialog(
      `Change the price of "${item.item_name}" from £${previousPrice} to £${nextPrice}?\n\n` +
      'This only affects new quotes and invoices from now on - anything already raised keeps its original price.'
    );
    if (!confirmed) return;

    setSavingId(item.id);
    try {
      await apiClient.put(`/api/PurchasableItems/${item.id}`, { item_price: nextPrice });

      setDisplayItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, item_price: nextPrice } : i)));
      setEditingId(null);
      setBanner({ type: 'success', text: `Updated "${item.item_name}" to £${nextPrice} (was £${previousPrice}).` });
    } catch (error) {
      console.error('Error updating price:', error);
      const message = error instanceof ApiError ? error.message : 'Could not update the price. Please try again.';
      setBanner({ type: 'error', text: message });
    } finally {
      setSavingId(null);
    }
  };

  const columns = [
    { key: 'item_name', label: 'Item', sortable: true },
    { key: 'item_description', label: 'Description' },
    { key: 'item_type', label: 'Item Type', sortable: true },
    { key: 'item_category_id', label: 'Category', sortable: true },
    { key: 'item_cost', label: 'Cost', sortable: true, sortValue: (item) => Number(item.item_cost) || 0, render: (item) => `£${item.item_cost}` },
    {
      key: 'item_price',
      label: 'Price (To Customer)',
      sortable: true,
      sortValue: (item) => Number(item.item_price) || 0,
      render: (item) => {
        if (!isSysadmin) return `£${item.item_price}`;
        if (editingId === item.id) {
          const saving = savingId === item.id;
          return (
            <span className="items-price-edit-row" onClick={(e) => e.stopPropagation()}>
              <span className="items-price-currency">£</span>
              <input
                type="number"
                step="0.01"
                min="0"
                autoFocus
                disabled={saving}
                className="items-price-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmAndSavePrice(item);
                  if (e.key === 'Escape') cancelEdit();
                }}
              />
              <button
                type="button"
                className="items-price-btn items-price-btn-save"
                disabled={saving}
                title="Save price"
                onClick={() => confirmAndSavePrice(item)}
              >
                <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-check'}></i>
              </button>
              <button
                type="button"
                className="items-price-btn items-price-btn-cancel"
                disabled={saving}
                title="Cancel"
                onClick={cancelEdit}
              >
                <i className="fas fa-times"></i>
              </button>
            </span>
          );
        }
        return (
          <span className="items-price-display" onClick={() => startEdit(item)} title="Click to change this item's price">
            £{item.item_price}
            <i className="fas fa-pencil items-price-pencil"></i>
          </span>
        );
      }
    },
    {
      key: 'profit',
      label: 'Profit',
      sortable: true,
      sortValue: (item) => Number(item.item_price) - Number(item.item_cost),
      render: (item) => {
        const profit = Number(item.item_price) - Number(item.item_cost);
        return <span className={`items-profit ${profit < 0 ? 'negative' : 'positive'}`}>£{profit.toFixed(2)}</span>;
      }
    }
  ];

  return (
    <PageShell>
      <div className="items-header">
        <h1>Saleable Items</h1>
      </div>
      {banner && (
        <div className={`items-banner items-banner-${banner.type}`} role="status">
          {banner.text}
          <button type="button" className="items-banner-close" onClick={() => setBanner(null)} aria-label="Dismiss">&times;</button>
        </div>
      )}
      {loading ? (
        <p>Loading items...</p>
      ) : (
        <ResponsiveTable columns={columns} rows={displayItems} rowKey="_key" emptyMessage="No saleable items found." />
      )}
    </PageShell>
  );
}

export default Items;
